// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getProject } from '@/project/api/get-project';
import { Project, ProjectLineItem } from '@/project/api/types';
import { useQuery } from '@tanstack/react-query';

interface UseProjectLineItemOptions {
  projectKey: null | string;
  lineItemKey: null | string;
}

interface UseProjectLineItemResult {
  project: null | Project;
  lineItem: null | ProjectLineItem;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

// Single-line-item lookup. The BE has no narrow endpoint, so we fetch
// the whole project and pluck the matching line item by key. Cached
// under a narrow key so the drawer container's view can be
// invalidated independently of the project listing — and so opening
// the drawer from the project detail page reuses the project's
// already-cached payload when present.
export function useProjectLineItem(
  options: UseProjectLineItemOptions
): UseProjectLineItemResult {
  const enabled = options.projectKey !== null && options.lineItemKey !== null;
  const query = useQuery({
    queryKey: ['project', options.projectKey, 'lineItem', options.lineItemKey],
    queryFn: async () => {
      if (options.projectKey === null) {
        throw new Error('projectKey is required');
      }
      const response = await getProject(options.projectKey);
      return response.project;
    },
    enabled,
  });

  const project = query.data ?? null;
  const lineItem =
    project === null
      ? null
      : (project.items.find((item) => item.key === options.lineItemKey) ??
        null);

  return {
    project,
    lineItem,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
